import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import {
    FindManufacturersQueryDTORuntype,
    FindManufacturersResponseDTORuntype,
    ImportManufacturerOutcomeRuntype,
    ManufacturerLineCardDTORuntype,
    ManufacturerLiteDTORuntype,
    ManufacturerWithPartCountDTORuntype,
} from './manufacturerBackendTypes';

export const manufacturerEndpoints = {
    'POST /manufacturers': endpoint({
        description: 'Creates a new manual manufacturer',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            name: z.string().max(100),
            alternative_names: z.array(z.string().max(100)).optional(),
        }),
        responseBody: z.object({ data: ManufacturerLiteDTORuntype }),
        invalidates: ['GET /manufacturers'],
        actionPermission: () => 'CanCreateManufacturer',
    }),

    'POST /manufacturers/import': endpoint({
        description: 'Imports manufacturers in bulk; failures are reported per item',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            items: z.array(
                z.object({
                    name: z.string().max(100),
                    alternative_names: z.array(z.string().max(100)).optional(),
                }),
            ),
        }),
        responseBody: z.object({ items: z.array(ImportManufacturerOutcomeRuntype) }),
        invalidates: ['GET /manufacturers'],
        actionPermission: () => 'CanCreateManufacturer',
    }),

    'GET /manufacturers': endpoint({
        description: 'Fetches all manufacturers',
        pathParams: z.undefined(),
        queryParams: z.object({
            high_quality: z.boolean().optional(),
            threshold: z.number().optional(),
        }),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(ManufacturerWithPartCountDTORuntype) }),
    }),

    'GET /manufacturers/line-card': endpoint({
        description: 'Fetches all linecards for every manufacturer',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.object({ data: z.array(ManufacturerLineCardDTORuntype) }),
    }),

    'POST /manufacturers/find': endpoint({
        description: 'Finds manufacturers by name or alias',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.object({
            queries: z.array(FindManufacturersQueryDTORuntype),
        }),
        responseBody: FindManufacturersResponseDTORuntype,
    }),

    'GET /manufacturers/:id/line-card': endpoint({
        description: 'Fetches linecards for that manufacturer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: ManufacturerLineCardDTORuntype,
    }),

    'PATCH /manufacturers/:id': endpoint({
        description: 'Renames a private manufacturer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ name: z.string().max(100) }),
        responseBody: z.null(),
        invalidates: ['GET /manufacturers'],
        actionPermission: () => 'CanUpdateManufacturer',
    }),

    'POST /manufacturers/:id/aliases': endpoint({
        description: 'Adds a private alias to a manufacturer',
        pathParams: z.object({ id: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.object({ name: z.string().max(100) }),
        responseBody: z.null(),
        invalidates: ['GET /manufacturers'],
        actionPermission: () => 'CanUpdateManufacturer',
    }),

    'DELETE /manufacturers/:id/aliases/:aliasName': endpoint({
        description: 'Removes an alias from a manufacturer',
        pathParams: z.object({ id: z.string(), aliasName: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.null(),
        invalidates: ['GET /manufacturers'],
        actionPermission: () => 'CanUpdateManufacturer',
    }),
};
